/**
 * The emoji picker.
 *
 * Replaces a row of twelve, which was reported — fairly — as "not all
 * that WhatsApp has". This is a categorised grid with tabs down the
 * side of the kind every messaging app has: recents first, then
 * smileys, people, animals, food, activities, travel, objects, symbols.
 *
 * It sits ABOVE the composer in normal flow, not floating over it. The
 * first version was absolutely positioned inside an element that was
 * not a positioning context, so it landed on top of the input: tapping
 * an emoji did insert it, and you could not see that happen because the
 * field was behind the panel. That is what "it does not put it on the
 * message input field" actually was.
 *
 * Recents are per-browser, in localStorage. They are a convenience, not
 * data — every read and write is wrapped, and the picker is correct
 * with none.
 */

import { useState } from 'react'
import './EmojiPicker.css'

const RECENT_KEY = 'ziv.emoji.recent'
const RECENT_MAX = 24

const GROUPS = [
  {
    id: 'smileys',
    label: 'Smileys',
    icon: '🙂',
    emoji: [
      '😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃','😉','😊','😇','🥰','😍','🤩',
      '😘','😗','😚','😙','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','🤐',
      '🤨','😐','😑','😶','😏','😒','🙄','😬','🤥','😌','😔','😪','🤤','😴','😷',
      '🤒','🤕','🤢','🤮','🤧','🥵','🥶','🥴','😵','🤯','🤠','🥳','😎','🤓','🧐',
      '😕','😟','🙁','☹️','😮','😯','😲','😳','🥺','😦','😧','😨','😰','😥','😢','😭',
      '😱','😖','😣','😞','😓','😩','😫','🥱','😤','😡','😠','🤬','😈','👿','💀','💩'
    ]
  },
  {
    id: 'people',
    label: 'People',
    icon: '👋',
    emoji: [
      '👋','🤚','🖐️','✋','🖖','👌','🤏','✌️','🤞','🤟','🤘','🤙','👈','👉','👆',
      '👇','☝️','👍','👎','✊','👊','🤛','🤜','👏','🙌','👐','🤲','🤝','🙏','✍️','💅',
      '💪','👂','👃','🧠','👀','👁️','👅','👄','💋','👶','🧒',
      '👦','👧','🧑','👨','👩','🧓','👴','👵','🙍','🙎','🙅','🙆','💁','🙋','🧏','🙇',
      '🤦','🤷','👮','🕵️','💂','👷','🤴','👸','👰','🤵','👼','🎅','🤶','🦸','🦹','🧙'
    ]
  },
  {
    id: 'animals',
    label: 'Animals',
    icon: '🐶',
    emoji: [
      '🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐨','🐯','🦁','🐮','🐷','🐽','🐸','🐵',
      '🙈','🙉','🙊','🐒','🐔','🐧','🐦','🐤','🐣','🐥','🦆','🦅','🦉','🦇','🐺','🐗',
      '🐴','🦄','🐝','🐛','🦋','🐌','🐞','🐜','🦗','🕷️','🦂','🐢','🐍','🦎',
      '🐙','🦑','🦐','🦀','🐡','🐠','🐟','🐬','🐳','🐋','🦈','🐊','🐅','🐆','🦓','🦍',
      '🐘','🦛','🦏','🐪','🐫','🦒','🦘','🐃','🐂','🐄','🐎','🐖','🐏','🐑','🦙','🐐'
    ]
  },
  {
    id: 'food',
    label: 'Food',
    icon: '🍎',
    emoji: [
      '🍏','🍎','🍐','🍊','🍋','🍌','🍉','🍇','🍓','🍈','🍒','🍑','🥭','🍍','🥥',
      '🥝','🍅','🍆','🥑','🥦','🥬','🥒','🌶️','🌽','🥕','🧄','🧅','🥔','🍠',
      '🥐','🥯','🍞','🥖','🥨','🧀','🥚','🍳','🧈','🥞','🧇','🥓','🥩','🍗','🍖','🌭',
      '🍔','🍟','🍕','🥪','🌮','🌯','🥙','🧆','🥘','🍲','🍜','🍝','🍛','🍣',
      '🍱','🥟','🦪','🍤','🍙','🍚','🍘','🍥','🥠','🍢','🍡','🍧','🍨','🍦','🥧','🍰',
      '🎂','🍮','🍭','🍬','🍫','🍿','🍩','🍪','☕','🍵','🧃','🥤','🍺','🍻','🥂','🍷'
    ]
  },
  {
    id: 'activity',
    label: 'Activity',
    icon: '⚽',
    emoji: [
      '⚽','🏀','🏈','⚾','🥎','🎾','🏐','🏉','🥏','🎱','🏓','🏸','🏒','🏑','🥍',
      '🏏','🥅','⛳','🏹','🎣','🤿','🥊','🥋','🎽','🛹','🛷','⛸️',
      '🎿','⛷️','🏂','🪂','🏋️','🤼','🤸','⛹️','🤺','🤾','🏌️','🏇','🧘','🏄','🏊','🤽',
      '🚣','🧗','🚴','🚵','🏆','🥇','🥈','🥉','🏅','🎖️','🎗️','🎫','🎟️','🎪','🎭','🎨',
      '🎬','🎤','🎧','🎼','🎹','🥁','🎷','🎺','🎸','🎻','🎲','♟️','🎯','🎳','🎮'
    ]
  },
  {
    id: 'travel',
    label: 'Travel',
    icon: '🚗',
    emoji: [
      '🚗','🚕','🚙','🚌','🚎','🏎️','🚓','🚑','🚒','🚐','🚚','🚛','🚜',
      '🛴','🚲','🛵','🏍️','🛺','🚨','🚔','🚍','🚘','🚖','🚡','🚠','🚟','🚃','🚋','🚞',
      '🚝','🚄','🚅','🚈','🚂','🚆','🚇','🚊','🚉','✈️','🛫','🛬','🛩️','💺','🛰️','🚀',
      '🛸','🚁','🛶','⛵','🚤','🛥️','🛳️','⛴️','🚢','⚓','⛽','🚧','🚦','🚥','🗺️',
      '🗿','🗽','🗼','🏰','🏯','🏟️','🎡','🎢','🎠','⛲','⛱️','🏖️','🏝️','🏜️','🌋','⛰️'
    ]
  },
  {
    id: 'objects',
    label: 'Objects',
    icon: '💡',
    emoji: [
      '⌚','📱','💻','⌨️','🖥️','🖨️','🖱️','💽','💾','💿','📀','📼','📷','📸','📹','🎥',
      '📞','☎️','📟','📠','📺','📻','🎙️','⏱️','⏲️','⏰','🕰️','⌛','⏳','📡','🔋','🔌',
      '💡','🔦','🕯️','🧯','🛢️','💸','💵','💴','💶','💷','💰','💳','💎','⚖️',
      '🧰','🔧','🔨','⚒️','🛠️','⛏️','🔩','⚙️','🧱','⛓️','🧲','🔫','💣',
      '🔪','🗡️','🛡️','🚬','⚰️','🏺','🔮','📿','🧿','💈','⚗️','🔭','🔬','🕳️','💊'
    ]
  },
  {
    id: 'symbols',
    label: 'Symbols',
    icon: '❤️',
    emoji: [
      '❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖',
      '💘','💝','💟','☮️','✝️','☪️','🕉️','☸️','✡️','🔯','🕎','☯️','☦️','🛐','⛎','♈',
      '♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓','🆔','⚛️','🉑','☢️','☣️',
      '📴','📳','🈶','🈚','🈸','🈺','🈷️','✴️','🆚','💮','🉐','㊙️','㊗️','🈴','🈵','🈹',
      '🅰️','🅱️','🆎','🆑','🅾️','🆘','❌','⭕','🛑','⛔','📛','🚫','💯','💢','♨️','🔥'
    ]
  }
]

function loadRecent () {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]')
    return Array.isArray(raw) ? raw.filter(e => typeof e === 'string').slice(0, RECENT_MAX) : []
  } catch {
    return []
  }
}

function saveRecent (list) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)))
  } catch {
    // Private windows and blocked site data. Recents are a convenience.
  }
}

export default function EmojiPicker ({ onPick, onClose }) {
  const [recent, setRecent] = useState(loadRecent)
  const [tab, setTab] = useState(() => (loadRecent().length ? 'recent' : 'smileys'))
  const [search, setSearch] = useState('')

  function pick (emoji) {
    const next = [emoji, ...recent.filter(e => e !== emoji)]
    setRecent(next)
    saveRecent(next)
    onPick(emoji)
  }

  const term = search.trim().toLowerCase()
  const shown = term
    // Searching looks across every group by its label, since there is no
    // per-emoji name list to match against without shipping one.
    ? GROUPS.filter(g => g.label.toLowerCase().includes(term)).flatMap(g => g.emoji)
    : tab === 'recent'
      ? recent
      : (GROUPS.find(g => g.id === tab)?.emoji ?? [])

  return (
    <div className="emp" role="group" aria-label="Emoji">
      <div className="emp__top">
        <input
          className="emp__search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search emoji"
          aria-label="Search emoji"
        />
        <button
          type="button"
          className="emp__close"
          onClick={onClose}
          aria-label="Close emoji"
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.9"
              strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="emp__grid">
        {shown.length === 0 ? (
          <p className="emp__none">
            {tab === 'recent' ? 'Emoji you use will appear here.' : 'Nothing to show.'}
          </p>
        ) : shown.map((emoji, i) => (
          <button
            key={`${emoji}-${i}`}
            type="button"
            className="emp__btn"
            onClick={() => pick(emoji)}
            data-testid="emoji-option"
          >
            {emoji}
          </button>
        ))}
      </div>

      <div className="emp__tabs" role="tablist" aria-label="Emoji categories">
        {recent.length > 0 && (
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'recent'}
            className={`emp__tab${tab === 'recent' ? ' is-on' : ''}`}
            onClick={() => { setTab('recent'); setSearch('') }}
            title="Recent"
          >
            🕘
          </button>
        )}
        {GROUPS.map(group => (
          <button
            key={group.id}
            type="button"
            role="tab"
            aria-selected={tab === group.id}
            className={`emp__tab${tab === group.id ? ' is-on' : ''}`}
            onClick={() => { setTab(group.id); setSearch('') }}
            title={group.label}
          >
            {group.icon}
          </button>
        ))}
      </div>
    </div>
  )
}
